import { useEffect, useState } from "react"
import { useMaterialUIController } from "context"

// @mui material components
import Grid from "@mui/material/Grid"
import CircularProgress from "@mui/material/CircularProgress"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
// Material Dashboard 2 React examples
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"
import MDButton from "components/MDButton"
import MDPagination from "components/MDPagination"
import MDTypography from "components/MDTypography"
import Icon from "@mui/material/Icon"
import TextField from "@mui/material/TextField"
import Collapse from "@mui/material/Collapse"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"

// Dispositivos page components
import DispositivosInformation from "layouts/dispositivos/components/DispositivosInformation"

//react-router-dom para direccionar
import { Link, useNavigate } from "react-router-dom"

//jwt decode para verificacion
import { jwtDecode } from "jwt-decode"
import { Card, Divider, FormControl, InputLabel, MenuItem, Select } from "@mui/material"
import ModalSelector from "components/ModalSelector"
import {
  fetchDeviceConfigs,
  fetchEmpresas,
  fetchEventos as fetchEventosService,
  fetchSucursales as fetchSucursalesService,
  fetchZonasBySucursal,
} from "./service"

function Dispositivos() {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const navigate = useNavigate()

  const [deviceData, setDeviceData] = useState(null)
  const [statusCheckVersion, setStatusCheckVersion] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [total, setTotal] = useState(0)
  const [ipFilter, setIpFilter] = useState("")
  const [empresas, setEmpresas] = useState([])
  const [sucursales, setSucursales] = useState([])
  const [eventos, setEventos] = useState([])
  const [zonas, setZonas] = useState([])
  const [selectedEmpresas, setSelectedEmpresas] = useState("")
  const [selectedSucursales, setSelectedSucursales] = useState("")
  const [selectedEventos, setSelectedEventos] = useState("")
  const [selectedZonas, setSelectedZonas] = useState("")
  const [nombreDispositivo, setNombreDispositivo] = useState("")
  const [appliedFilters, setAppliedFilters] = useState({
    ip: "",
    empresaId: "",
    sucursalId: "",
    eventoId: "",
    zonaId: "",
    nombreDispositivo: "",
  })

  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  const fetchPageData = async (page, filters = appliedFilters) => {
    setLoading(true)
    try {
      if (!token) return
      const result = await fetchDeviceConfigs(page, 10, {
        ip: filters.ip,
        empresaId: filters.empresaId,
        sucursalId: filters.sucursalId,
        eventoId: filters.eventoId,
        zonaId: filters.zonaId,
        nombreDispositivo: filters.nombreDispositivo,
      })

      if (result?._embedded) {
        setDeviceData(result._embedded.configDeviceList)
      } else {
        setDeviceData([])
      }
      setStatusCheckVersion((prev) => prev + 1)
      setTotal(result?.page?.totalPages ?? 0)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const gotoPage = (p) => {
    setPageIndex(p)
    fetchPageData(p)
  }

  const reloadDevices = () => {
    gotoPage(0)
  }

  const getEmpresas = async () => {
    try {
      const result = await fetchEmpresas()
      if (result) {
        setEmpresas(result)
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const fetchSucursales = async (selectedId) => {
    try {
      if (selectedId) {
        const result = await fetchSucursalesService(selectedId)
        setSelectedSucursales("")
        if (result) {
          setSucursales(result)
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const fetchEventos = async (selectedId) => {
    try {
      if (selectedId) {
        const result = await fetchEventosService(selectedId)
        setSelectedEventos("")
        if (result) {
          setEventos(result)
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const resetValues = () => {
    setIpFilter("")
    setSelectedEmpresas("")
    setSelectedSucursales("")
    setSelectedEventos("")
    setSelectedZonas("")
    setNombreDispositivo("")
  }

  const handleClearFilters = () => {
    resetValues()
  }

  const fetchZonas = async (selectedId) => {
    try {
      if (selectedId) {
        const result = await fetchZonasBySucursal(selectedId)
        setSelectedZonas("")
        if (result) {
          setZonas(result)
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const handleEmpresasChange = (e) => {
    const selectedId = e.target.value
    setSelectedEmpresas(selectedId)
    fetchSucursales(selectedId)
    fetchEventos(selectedId)
    setSelectedZonas("")
  }

  const handleSucursalesChange = (selectedId) => {
    setSelectedSucursales(selectedId)
    fetchZonas(selectedId)
  }

  const handleEventosChange = (e) => {
    const selectedId = e.target.value
    setSelectedEventos(selectedId)
    fetchZonas(selectedId)
  }

  const PaginationItems = () => {
    const pages = []

    // Calculate the start and end page indices
    let startPage = Math.max(pageIndex - 1, 0)
    let endPage = Math.min(pageIndex + 2, total)

    // Populate the pages array with page numbers
    for (let i = startPage; i < endPage; i++) {
      pages.push(i)
    }

    // Add ellipsis at the beginning if needed
    if (startPage > 0) {
      pages.unshift("...")
      pages.unshift(0)
    }

    // Add ellipsis at the end if needed
    if (endPage < total) {
      pages.push("...")
      pages.push(total - 1) // Ensure the last page is always shown
    }

    return (
      <>
        {pages.map((i, idx) =>
          i === "..." ? (
            <span key={idx} style={{ margin: "0 8px" }}>
              ...
            </span>
          ) : (
            <MDPagination item key={i} onClick={() => gotoPage(i)} active={pageIndex === i}>
              <MDTypography
                variant="caption"
                color={pageIndex === i ? "white" : darkMode ? "white" : "secondary"}
                sx={{ fontSize: 11 }}
              >
                {i + 1 >= 1000000
                  ? ((i + 1) / 1000000).toFixed(0) + "M"
                  : i + 1 >= 1000
                    ? ((i + 1) / 1000).toFixed(0) + "K"
                    : i + 1}
              </MDTypography>
            </MDPagination>
          )
        )}
      </>
    )
  }

  const handleIpFilterSearch = (event) => {
    const term = event.target.value
    setIpFilter(term)
  }

  const handleNombreDispositivoFilterSearch = (event) => {
    const term = event.target.value
    setNombreDispositivo(term)
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      fetchPageData(0)
    }
  }

  const handleBuscar = async () => {
    const nextFilters = {
      ip: ipFilter,
      empresaId: selectedEmpresas,
      sucursalId: selectedSucursales,
      eventoId: selectedEventos,
      zonaId: selectedZonas,
      nombreDispositivo,
    }

    setAppliedFilters(nextFilters)
    setPageIndex(0)
    await fetchPageData(0, nextFilters)
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  useEffect(() => {
    getEmpresas()
  }, [])

  useEffect(() => {
    fetchPageData(0)
  }, [])

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={8}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Card>
                <MDBox px={2}>
                  <MDBox display="flex" alignItems="center" py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                      Filtros
                    </MDTypography>{" "}
                    <MDBox display="flex" alignItems="flex-start">
                      <MDBox
                        display="flex"
                        alignItems="center"
                        onClick={handleExpandClick}
                        sx={{ cursor: "pointer" }}
                      >
                        {expanded ? (
                          <ExpandLessIcon fontSize="medium" />
                        ) : (
                          <ExpandMoreIcon fontSize="medium" />
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} pb={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Empresa</InputLabel>
                          <Select
                            label="Empresa"
                            variant="outlined"
                            fullWidth
                            value={selectedEmpresas}
                            onChange={handleEmpresasChange}
                            sx={{ p: 1.5 }}
                            placeholder="Seleccione.."
                          >
                            <MenuItem value="">Ninguna</MenuItem>
                            {empresas.map((emp) => (
                              <MenuItem key={emp.idEmpresa} value={emp.idEmpresa}>
                                {emp.descripcion}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ModalSelector
                          value={selectedSucursales}
                          onChange={handleSucursalesChange}
                          items={sucursales}
                          itemValue="idSucursal"
                          itemLabel="descripcion"
                          label="Sucursal"
                          placeholder="Seleccione una sucursal"
                          searchPlaceholder="Buscar sucursal..."
                          emptyText="No se encontraron sucursales"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ModalSelector
                          value={selectedEventos}
                          onChange={(selectedId) =>
                            handleEventosChange({ target: { value: selectedId } })
                          }
                          items={eventos}
                          itemValue="idEvento"
                          itemLabel="descripcion"
                          label="Evento"
                          placeholder="Seleccione un evento"
                          searchPlaceholder="Buscar evento..."
                          emptyText="No se encontraron eventos"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <ModalSelector
                          value={selectedZonas}
                          onChange={setSelectedZonas}
                          items={zonas}
                          itemValue="idZona"
                          itemLabel="descripcion"
                          label="Zona"
                          placeholder="Seleccione una zona"
                          searchPlaceholder="Buscar zona..."
                          emptyText="No se encontraron zonas"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="IP"
                          variant="outlined"
                          fullWidth
                          value={ipFilter}
                          onChange={handleIpFilterSearch}
                          onKeyDown={handleKeyDown}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Nombre de dispositivo"
                          variant="outlined"
                          fullWidth
                          value={nombreDispositivo}
                          onChange={handleNombreDispositivoFilterSearch}
                          onKeyDown={handleKeyDown}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={2}
                        >
                          <MDBox>
                            <MDButton
                              type="submit"
                              variant="gradient"
                              color="info"
                              onClick={handleBuscar}
                            >
                              Buscar
                            </MDButton>
                            &nbsp;
                            <MDButton
                              type="button"
                              variant="outlined"
                              color="info"
                              onClick={handleClearFilters}
                            >
                              Limpiar
                            </MDButton>
                          </MDBox>

                          <Link to="/dispositivos/agregar" style={{ textDecoration: "none" }}>
                            <MDButton type="button" variant="gradient" color="success">
                              Agregar
                            </MDButton>
                          </Link>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Collapse>
                </MDBox>
              </Card>
              <Divider></Divider>
              <Card pt={5} mt={3}>
                <MDBox mt={2} pr={2}>
                  <MDPagination>
                    {total * 10 > 10 && (
                      <MDTypography
                        variant="overline"
                        color="light"
                        fontWeight="light"
                        verticalAlign="middle"
                      >
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          10 * pageIndex + 1
                        )}{" "}
                        a{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          (pageIndex + 1) * 10
                        )}{" "}
                        de{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          total * 10
                        )}
                        &nbsp;&nbsp;
                      </MDTypography>
                    )}
                    {pageIndex > 0 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex - 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                    )}
                    <PaginationItems />
                    {pageIndex < total - 1 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex + 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    )}
                  </MDPagination>
                </MDBox>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : deviceData && deviceData.length > 0 ? (
                  <DispositivosInformation
                    dispositivos={deviceData}
                    onDelete={reloadDevices}
                    statusCheckVersion={statusCheckVersion}
                  />
                ) : deviceData && deviceData.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <MDTypography variant="h6" color="text">
                      No hay datos para mostrar
                    </MDTypography>
                  </MDBox>
                ) : null}
                <MDBox mb={2} pr={2}>
                  <MDPagination>
                    {total * 10 > 10 && (
                      <MDTypography
                        variant="overline"
                        color="light"
                        fontWeight="light"
                        verticalAlign="middle"
                      >
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          10 * pageIndex + 1
                        )}{" "}
                        a{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          (pageIndex + 1) * 10
                        )}{" "}
                        de{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          total * 10
                        )}
                        &nbsp;&nbsp;
                      </MDTypography>
                    )}
                    {pageIndex > 0 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex - 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                    )}
                    <PaginationItems />
                    {pageIndex < total - 1 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex + 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    )}
                  </MDPagination>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default Dispositivos
